<template>
  <main class="pp-page--delivery-details">
    <h1 class="title title--desktop">{{ $t('message.ticket_delivery') }}</h1>
    <div class="pp-layout-container form-page__main">
      <div class="pp-preamble">
        <h1 class="title title--mobile">{{ $t('message.ticket_delivery') }}</h1>
      </div>
      <section class="pp-main-content">
        <form class="delivery-details-form form-common-style" id="delivery-details">
          <div v-if="specificCountryWarning.length > 0">
            <p v-t="specificCountryWarning"/>
          </div>
          <div class="input-wrapper required delivery-details--country-code">
            <select v-model="address.country_code" :disabled="specificCountryWarning.length > 0">
              <option value="" disabled selected>{{ $t('delivery_details.select_a_country' )}}</option>
              <option v-for="(country, key) in countryCodesToDisplay" :key="key"
                :value="key">{{ country }}</option>
            </select>
          </div>
          <div class="forms__field-wrapper">
            <div class="input-wrapper required">
              <input
                class="input input--address-line-one"
                type="text"
                :placeholder="$t('delivery_details.delivery_address_1')"
                :id="inputId('delivery_address_1')"
                :value="address.deliveryAddress1"
                @input="address.deliveryAddress1 = $event.target.value"
              />
              <label
                class="floating-label"
                v-t="'delivery_details.delivery_address_1'"
                :for="inputId('delivery_address_1')"
              />
            </div>
          </div>
          <div class="forms__field-wrapper">
            <div class="input-wrapper">
              <input
                class="input input--address-line-two"
                type="text"
                :placeholder="$t('delivery_details.delivery_address_2')"
                :id="inputId('delivery_address_2')"
                :value="address.deliveryAddress2"
                @input="address.deliveryAddress2 = $event.target.value"
              />
              <label
                class="floating-label"
                v-t="'delivery_details.delivery_address_2'"
                :for="inputId('delivery_address_2')"
              />
            </div>
          </div>
          <div class="forms__field-wrapper">
            <div class="input-wrapper">
              <input
                class="input input--address-line-three"
                type="text"
                :placeholder="$t('delivery_details.delivery_address_3')"
                :id="inputId('delivery_address_3')"
                :value="address.deliveryAddress3"
                @input="address.deliveryAddress3 = $event.target.value"
              />
              <label
                class="floating-label"
                v-t="'delivery_details.delivery_address_3'"
                :for="inputId('delivery_address_3')"
              />
            </div>
          </div>
          <div class="forms__field-wrapper">
            <div class="input-wrapper">
              <input
                class="input input--postcode"
                type="text"
                :placeholder="$t('delivery_details.postcode')"
                :id="inputId('postcode')"
                :value="address.postcode"
                @input="address.postcode = $event.target.value"
              />
              <label
                class="floating-label"
                v-t="'delivery_details.postcode'"
                :for="inputId('postcode')"
              />
            </div>
          </div>
        </form>
      </section>
     <!-- Mobile actions -->
      <div class="pp-actions">
        <div class="pp-actions__container">
          <fancy-button
            :text="$t('buttons.payment_method')"
            @click.native="submitAddress"
            :icon="['far', 'ticket-alt']"
            :disabled="disabledAddressCTA"/>
        </div>
      </div>
      <aside class="pp-sidebar">
        <fancy-button
          :text="$t('buttons.payment_method')"
          @click.native="submitAddress"
          :icon="['far', 'ticket-alt']"
          :disabled="disabledAddressCTA"/>
      </aside>
    </div>
  </main>
</template>

<script>
import FancyButton from "@/components/generic/FancyButton";
import { mapState, mapActions } from "vuex"
import countries from "@/util/countries.json"

export default {
  name: "delivery-details",
  components: {
    FancyButton
  },
  mounted() {
    // Setting loading to true when we click checkout on shopping cart
    this.updateLoading(false)
  },
  data() {
    return {
      address: {
        deliveryAddress1: "",
        deliveryAddress2: "",
        deliveryAddress3: "",
        post_code: "",
        country_code: ""
      },
    };
  },
  watch: {
    "$i18n.locale": function() {
      this.initCreated();
    }
  },
  async created() {
    await this.initCreated();
  },
  computed: {
    countryCodesToDisplay() {
      switch(this.$i18n.locale) {
        case 'tc':
          return countries.tc
        case 'sc':
          return countries.sc
        default:
          return countries.en
      }
    },
    specificCountryWarning() {
      let activeDeliveryCode = this.cart.deliveryOptions[this.cart.activeDeliveryOptionIndex].code

      switch(activeDeliveryCode) {
        case 'REGISTERED_MAIL_HK':
        case 'COURIER_HK':
        case 'KIOSK_OFFICE_PICKUP_WKCD':
          this.address.country_code = 'HK'
          return 'delivery_warning.hong_kong_only'
        case 'COURIER_MC':
          this.address.country_code = 'MO'
          return 'delivery_warning.macau_only'
        default:
          return ''
      }
    },
    disabledAddressCTA() {
      return this.address.deliveryAddress1.length == 0 || this.address.country_code.length == 0;
    },
    ...mapState(['cart', 'user'])
  },
  methods: {
    async initCreated() {
      this.updateStage("delivery-details")
      // check to see if we've already saved the delivery details
      if (Object.keys(this.user.deliveryDetails).length === 0) {
        await this.retrieveUserDetails();

        if (!this.specificCountryWarning.length > 0) {
          Object.keys(this.countryCodesToDisplay).forEach((key) => {
            if (this.countryCodesToDisplay[key] === this.user.userDetails.deliveryAddressCountry) {
              this.address.country_code = key
            }
          });
        }

        // Bind initial values (in case there's no modifications)
        this.address.deliveryAddress1 = this.user.userDetails.deliveryAddress1;
        this.address.deliveryAddress2 = this.user.userDetails.deliveryAddress2;
        this.address.deliveryAddress3 = this.user.userDetails.deliveryAddress3;
      } else {
        this.address = this.user.deliveryDetails;
      }
    },
    submitAddress() {
      this.updateLoading(true);
      this.updateDeliveryInformation(this.address);
      this.scrollToTopOfPurchasePath();
      this.$router.push({ name: "payment", params: { locale: this.$i18n.locale } });
    },
    emitAddress() {
      if (this.active) {
        this.$emit("addressUpdate", this.address);
      }
    },
    inputId(field) {
      return field;
    },
    placeholderText(field) {
      return this.$t("delivery_details." + field);
    },
    backToCart() {
      this.$router.push({ name: "shopping-cart", params: { locale: this.$i18n.locale } });
    },
    ...mapActions([
      'updateStage',
      'updateDeliveryInformation',
      'retrieveUserDetails',
      'updateLoading',
      'scrollToTopOfPurchasePath'
    ])
  },
};
</script>